import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import notifications from "../common/Components/Notifications/Notification";
import HKPConfirmDialog from "../common/Components/HKPConfirmDialog";
import { canUseCare } from "../helpers/careEnabled";
import { readAuthUser } from "../helpers/studioModules";
import CareCardPrint from "./CareCardPrint";
import CareView from "./CareView";
import { applyLatestProductCare, getProductCare, listCareTemplates, removeProductCare, saveProductCare } from "./careApi";
import { careSectionLabels, productCareCopy as copy } from "./careCopy";
import { CARE_SECTION_KEYS } from "./careTypes";
import type { CareLang, CareSectionKey, CareSections, CareTemplate, CareText, ProductCare } from "./careTypes";
import { filledSectionCount, isBlankText, pickText, resolveCare } from "./careValues";
import "./care.scss";

type Props = { productUniqueId?: string; productName?: string };

type Draft = {
  templateId: string;
  /** Template text the product starts from: the saved snapshot, or the newly chosen template. */
  base: CareSections;
  baseVersion: number | null;
  templateChanged: boolean;
  overrides: CareSections;
  /** The product's own changes when editing started, so "keep" can bring them back. */
  previous: CareSections;
  notes: CareText;
  keepOverrides: boolean;
};

/**
 * Care & Preservation inside the product form. Saves on its own (it is not part of the product Save),
 * so a product without care behaves exactly as before.
 */
const ProductCareSection = ({ productUniqueId, productName }: Props) => {
  const enabled = canUseCare(readAuthUser()?.users?.role) && Boolean(productUniqueId);
  const [care, setCare] = useState<ProductCare | null>(null);
  const [templates, setTemplates] = useState<CareTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [lang, setLang] = useState<CareLang>("en");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; reload?: boolean } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    if (!productUniqueId) return;
    setLoading(true);
    setLoadError("");
    const [careRes, listRes] = await Promise.all([getProductCare(productUniqueId), listCareTemplates({ status: "ACTIVE" })]);
    if (careRes.success && careRes.data) setCare(careRes.data);
    else setLoadError(careRes.message || "Care could not be loaded.");
    if (listRes.success) setTemplates(listRes.data || []);
    else if (careRes.success) setLoadError(listRes.message || "Care templates could not be loaded.");
    setLoading(false);
  }, [productUniqueId]);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  const profile = care?.profile || null;

  const startNew = () => {
    setMessage(null);
    setDraft({ templateId: "", base: {}, baseVersion: null, templateChanged: true, overrides: {}, previous: {}, notes: { en: "", hi: "" }, keepOverrides: true });
  };

  const startEdit = () => {
    if (!profile) return;
    setMessage(null);
    setDraft({
      templateId: profile.templateId,
      base: profile.templateSnapshot || {},
      baseVersion: profile.templateVersion,
      templateChanged: false,
      overrides: { ...(profile.overrides || {}) },
      previous: { ...(profile.overrides || {}) },
      notes: { en: profile.additionalNotes?.en || "", hi: profile.additionalNotes?.hi || "" },
      keepOverrides: true,
    });
  };

  const chooseTemplate = (templateId: string) => {
    if (!draft) return;
    const template = templates.find((t) => t.id === templateId);
    if (!template) {
      setDraft({ ...draft, templateId: "", base: {}, baseVersion: null });
      return;
    }
    const backToSaved = profile && profile.templateId === templateId;
    setDraft({
      ...draft,
      templateId,
      base: backToSaved ? profile!.templateSnapshot : template.sections,
      baseVersion: backToSaved ? profile!.templateVersion : template.version,
      templateChanged: !backToSaved,
    });
  };

  const hasOwnChanges = Boolean(draft && Object.keys(draft.previous).length > 0);
  const effectiveOverrides: CareSections = draft ? draft.overrides : {};
  const preview = useMemo(
    () => (draft ? resolveCare(draft.base, effectiveOverrides, draft.notes) : care?.resolved || null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draft, care]
  );

  const setOverride = (key: CareSectionKey, value: CareText | undefined) => {
    if (!draft) return;
    const next = { ...draft.overrides };
    if (value === undefined) delete next[key];
    else next[key] = value;
    setDraft({ ...draft, overrides: next });
  };

  const handleFailure = (code?: string, text?: string) => {
    if (code === "VERSION_CONFLICT" || code === "PROFILE_EXISTS") {
      setMessage({ text: copy.conflict, reload: true });
    } else if (code === "TEMPLATE_CHANGED") {
      setMessage({ text: copy.templateChanged });
      void listCareTemplates({ status: "ACTIVE" }).then((res) => {
        if (!res.success) return;
        setTemplates(res.data || []);
        const fresh = (res.data || []).find((t) => t.id === draft?.templateId);
        if (fresh && draft) setDraft({ ...draft, base: fresh.sections, baseVersion: fresh.version });
      });
    } else if (code === "CARE_EMPTY") {
      setMessage({ text: copy.empty });
    } else {
      setMessage({ text: text || "Care could not be saved. Please try again." });
    }
  };

  const save = async () => {
    if (!draft || !productUniqueId || !draft.templateId) return;
    setSaving(true);
    setMessage(null);
    const res = await saveProductCare(productUniqueId, {
      templateId: draft.templateId,
      expectedTemplateVersion: draft.baseVersion,
      expectedProfileVersion: profile ? profile.version : null,
      overrides: effectiveOverrides,
      additionalNotes: isBlankText(draft.notes) ? null : draft.notes,
    });
    setSaving(false);
    if (res.success && res.data) {
      setCare(res.data);
      setDraft(null);
      notifications({ title: "Success!", message: copy.saved, type: "success" });
      return;
    }
    handleFailure(res.code, res.message);
  };

  const applyLatest = async () => {
    if (!profile || !productUniqueId) return;
    setSaving(true);
    const res = await applyLatestProductCare(productUniqueId, profile.version);
    setSaving(false);
    if (res.success && res.data) {
      setCare(res.data);
      notifications({ title: "Success!", message: copy.saved, type: "success" });
    } else handleFailure(res.code, res.message);
  };

  const remove = async () => {
    if (!profile || !productUniqueId) return;
    setSaving(true);
    const res = await removeProductCare(productUniqueId, profile.version);
    setSaving(false);
    setConfirmRemove(false);
    if (res.success && res.data) {
      setCare(res.data);
      setDraft(null);
    } else handleFailure(res.code, res.message);
  };

  if (!enabled) return null;

  const usable = templates.filter((t) => filledSectionCount(t.sections) > 0);
  const studioName = readAuthUser()?.users?.organizationResponse?.name;

  return (
    <div className="UserDetailsDiv mb-5 hkp-product-care" id="hkp-product-care">
      <div className="UserHeader headerDisplay mb-2">
        <div>{copy.title}</div>
        <Link to="/care-templates" className="small hkp-product-care__manage">
          {copy.manageTemplates}
        </Link>
      </div>
      <div className="hkp-product-care__body">

      {loading ? (
        <p className="text-muted mb-0" role="status">
          Loading care…
        </p>
      ) : loadError ? (
        <p className="RequiredField mb-0" role="alert">
          {loadError}{" "}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => void load()}>
            Retry
          </button>
        </p>
      ) : draft ? (
        <div className="hkp-product-care__edit">
          <Label htmlFor="hkp-product-care-template">{copy.chooseTemplate}</Label>
          <select id="hkp-product-care-template" className="form-select" value={draft.templateId} onChange={(e) => chooseTemplate(e.target.value)}>
            <option value="">{copy.choosePlaceholder}</option>
            {profile && !usable.some((t) => t.id === profile.templateId) && <option value={profile.templateId}>{profile.templateName}</option>}
            {usable.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.source === "GLOBAL" ? " · HKP library" : ""}
              </option>
            ))}
          </select>
          {usable.length === 0 && !profile && <p className="text-muted small mt-1 mb-0">{copy.noTemplates}</p>}

          {draft.templateChanged && hasOwnChanges && draft.templateId && (
            <fieldset className="hkp-product-care__choice">
              <legend>{copy.changesQuestion}</legend>
              <Label check>
                <Input type="radio" name="hkp-care-keep" checked={draft.keepOverrides} onChange={() => setDraft({ ...draft, keepOverrides: true, overrides: { ...draft.previous } })} /> {copy.keepChanges}
              </Label>
              <Label check>
                <Input type="radio" name="hkp-care-keep" checked={!draft.keepOverrides} onChange={() => setDraft({ ...draft, keepOverrides: false, overrides: {} })} /> {copy.clearChanges}
              </Label>
            </fieldset>
          )}

          {draft.templateId && (
            <>
              <div className="hkp-care-editor__lang" role="tablist" aria-label="Language">
                {(["en", "hi"] as CareLang[]).map((l) => (
                  <button key={l} type="button" role="tab" aria-selected={lang === l} className={lang === l ? "is-active" : ""} onClick={() => setLang(l)}>
                    {l === "en" ? "English" : "हिंदी"}
                  </button>
                ))}
              </div>
              {CARE_SECTION_KEYS.map((key) => {
                const own = effectiveOverrides[key];
                const baseText = draft.base[key];
                if (isBlankText(baseText) && !own) return null;
                const hidden = Boolean(own) && isBlankText(own);
                return (
                  <div className="hkp-product-care__row" key={key}>
                    <div className="hkp-product-care__row-head">
                      <strong>{careSectionLabels[key][lang]}</strong>
                      <div className="hkp-product-care__row-actions">
                        {own ? (
                          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setOverride(key, undefined)}>
                            {copy.useTemplate}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            onClick={() => setOverride(key, { en: baseText?.en || "", hi: baseText?.hi || "" })}
                          >
                            {copy.customise}
                          </button>
                        )}
                        <Label check className="small mb-0">
                          <Input
                            type="checkbox"
                            checked={hidden}
                            onChange={(e) => setOverride(key, e.target.checked ? { en: "", hi: "" } : undefined)}
                          />{" "}
                          {copy.hide}
                        </Label>
                      </div>
                    </div>
                    {hidden ? (
                      <p className="text-muted small mb-0">{copy.hidden}</p>
                    ) : own ? (
                      <Input
                        type="textarea"
                        rows={4}
                        maxLength={1500}
                        lang={lang}
                        aria-label={`${careSectionLabels[key].en} (${lang === "en" ? "English" : "Hindi"})`}
                        value={(own[lang] as string) || ""}
                        onChange={(e) => setOverride(key, { ...own, [lang]: e.target.value })}
                      />
                    ) : (
                      <p className="hkp-product-care__base">{pickText(baseText, lang)}</p>
                    )}
                  </div>
                );
              })}
              <Label htmlFor="hkp-product-care-notes" className="mt-2">
                {copy.notes}
              </Label>
              <Input
                id="hkp-product-care-notes"
                type="textarea"
                rows={2}
                maxLength={1000}
                lang={lang}
                value={(draft.notes[lang] as string) || ""}
                onChange={(e) => setDraft({ ...draft, notes: { ...draft.notes, [lang]: e.target.value } })}
              />
              <details className="hkp-product-care__preview">
                <summary>Preview what buyers see</summary>
                <CareView care={preview} lang={lang} />
              </details>
            </>
          )}

          {message && (
            <p className="RequiredField" role="alert">
              {message.text}{" "}
              {message.reload && (
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => {
                    setDraft(null);
                    setMessage(null);
                    void load();
                  }}
                >
                  Reload
                </button>
              )}
            </p>
          )}
          <div className="hkp-product-care__buttons">
            <Button className="btn-hastkala-outline" disabled={saving} onClick={() => setDraft(null)}>
              {copy.cancel}
            </Button>
            <Button color="primary" disabled={saving || !draft.templateId} aria-busy={saving} onClick={() => void save()}>
              {saving ? copy.saving : copy.save}
            </Button>
          </div>
        </div>
      ) : profile ? (
        <>
          <p className="text-muted small mb-2">{copy.fromTemplate(profile.templateName)}</p>
          {profile.updateAvailable && (
            <div className="hkp-care-editor__alert" role="status">
              <span>{copy.updateAvailable}</span>
              <Button size="sm" color="primary" outline disabled={saving} onClick={() => void applyLatest()}>
                {copy.applyLatest}
              </Button>
            </div>
          )}
          {profile.templateArchived && <p className="text-muted small">{copy.archivedNote}</p>}
          <CareView care={care?.resolved} lang={lang} hideTitle collapsible />
          {message && (
            <p className="RequiredField" role="alert">
              {message.text}
            </p>
          )}
          <div className="hkp-product-care__buttons is-start">
            <Button size="sm" className="btn-hastkala-outline" onClick={startEdit}>
              {copy.edit}
            </Button>
            <Button size="sm" className="btn-hastkala-outline" onClick={() => setPrinting(true)}>
              {copy.print}
            </Button>
            <Button size="sm" color="danger" outline onClick={() => setConfirmRemove(true)}>
              {copy.remove}
            </Button>
          </div>
        </>
      ) : (
        <div className="hkp-product-care__empty">
          <p className="text-muted mb-2">{copy.notSet}</p>
          <Button size="sm" color="primary" outline onClick={startNew}>
            {copy.add}
          </Button>
        </div>
      )}

      </div>
      <HKPConfirmDialog
        open={confirmRemove}
        title={copy.removeTitle}
        description={copy.removeBody}
        confirmLabel={copy.remove}
        busy={saving}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() => void remove()}
      />
      {printing && care?.resolved && productUniqueId && (
        <CareCardPrint
          open={printing}
          onClose={() => setPrinting(false)}
          care={care.resolved}
          productName={productName}
          studioName={studioName}
          productUniqueId={productUniqueId}
        />
      )}
    </div>
  );
};

export default ProductCareSection;
